import {
    resourceGetList,
    resourcePostList,
    resourcePatchDetail,
    sendAction,
} from "shared/djangio/action-creators"
import {
    loadStanceCustomLabelsActionDict,
    updateStanceCustomLabelActionDict,
    createStanceCustomLabelActionDict,
    updateStanceAlertNameActionDict,
} from "shared/stance/action-types"

/**
* This function does an initial fetch of the StanceCustomLabels for the current User, and then saves it in the Redux store.
* The user will need to navigate to a Profile, Sheet, or the stance settings form before it's needed,
* so we can reduce the time to interactive by making it an asynchronous fetch on the initial page load.
*
* When loading a widget that actually requires this data, we can fire another request to ensure the user sees the most up to date
* information - in the rare occasions when this collection has changed since first load.
* @name getStanceCustomLabels
* @function
*/
export const getStanceCustomLabels = () => (
    resourceGetList(
        DjangIO.app.userdata.models.LegislativeStanceCustomLabel,
        loadStanceCustomLabelsActionDict,
        {},
        {},
        true
    )
)

/**
* This function updates a single LegislativeStanceCustomLabel
* @param {number} stanceCustomLabelId - ID of the LegislativeStanceCustomLabel to be updated
* @param {number} userId - ID of user who is updating the LegislativeStanceCustomLabel
* @param {string} newLabel - Value to set as label for LegislativeStanceCustomLabel
* @name updateStanceCustomLabel
* @function
*/
export const updateStanceCustomLabel = ({
    stanceCustomLabelId,
    userId,
    newLabel,
}) => (
    resourcePatchDetail(
        DjangIO.app.userdata.models.LegislativeStanceCustomLabel,
        updateStanceCustomLabelActionDict,
        stanceCustomLabelId,
        {
            user: DjangIO.app.userdata.models.QuorumUser.foreignKey(userId),
            label: newLabel,
        }
    )
)

/**
* This function creates a new LegislativeStanceCustomLabel with a POST request
* @param {number} stanceValue - value representing a LegislativeStanceType which
*    will be the stance on the new LegislativeStanceCustomLabel
* @param {string} label - value to set as label for the new LegislativeStanceCustomLabel
* @param {number} userId - ID of user who is creating the LegislativeStanceCustomLabel
* @param {number} orgId - ID of org which this LegislativeStanceCustomLabel will pertain to
* @name createStanceCustomLabel
* @function
*/
export const createStanceCustomLabel = ({
    stanceValue,
    label,
    userId,
    orgId,
}) => (
    resourcePostList(
        DjangIO.app.userdata.models.LegislativeStanceCustomLabel,
        createStanceCustomLabelActionDict,
        {
            stance: stanceValue,
            user: DjangIO.app.userdata.models.QuorumUser.foreignKey(userId),
            organization: DjangIO.app.userdata.models.Organization.foreignKey(orgId),
            label,
        },
        {},
    )
)

/**
* This function updates a LegislativeStanceCustomLabel by calling updateStanceCustomLabel if a stanceCustomLabelId is passed
* if not, this function calls createStanceCustomLabel to creates a new LegislativeStanceCustomLabel
*
* @param {number} stanceCustomLabelId - ID of the LegislativeStanceCustomLabel to be updated
* @param {number} stanceValue - value representing a LegislativeStanceType which
*    will be the stance on the LegislativeStanceCustomLabel
* @param {string} label - value to set as label for the LegislativeStanceCustomLabel
* @param {number} userId - ID of user who is creating/updating the LegislativeStanceCustomLabel
* @param {number} orgId - ID of org which this LegislativeStanceCustomLabel pertains to
* @name createOrUpdateStanceCustomLabel
* @function
*/export const createOrUpdateStanceCustomLabel = ({
    stanceCustomLabelId,
    stanceValue,
    newLabel,
    userId,
    orgId,
}) => {
    if (stanceCustomLabelId) {
        return updateStanceCustomLabel({
            stanceCustomLabelId,
            newLabel,
            userId,
        })
    }
    return createStanceCustomLabel({
        stanceValue,
        label: newLabel,
        userId,
        orgId
    })
}

/**
* This is the same as getStanceCustomLabels above, but it goes through the /api/sheet/ endpoint.
*
* We need to go through the Sheets endpoint, because External Sheet users are not authenticated,
* but the Sheets endpoint is whitelisted in app.api.classes.QuorumSessionAuthentication.is_authenticated()
*
* @name getStanceCustomLabelsForExternalSheets
* @function
* @param {string} sheetKey - An external sheet slug. We include this so that is_authenticated() can attach a user to the Request object,
    and we can get the appropriate AlignmentTypes through that User's Organization.
*/
export const getStanceCustomLabelsForExternalSheets = sheetKey => (
    sendAction(
        DjangIO.app.userdata.spreadsheet.models.Sheet.objects.get(sheetKey),
        loadStanceCustomLabelsActionDict,
        {
            action: "get_stance_custom_labels",
            kwargs: {},
            method: "get",
            payload: {}
        }
    )
)

/**
* This is the same as getStanceCustomLabels above, but it goes through the /api/dashboard/ endpoint.
*
* We need to go through the Dashboard endpoint, because External Dashboard users are not authenticated,
* but the Dashboards endpoint is whitelisted in app.api.classes.QuorumSessionAuthentication.is_authenticated()
*
* @name getStanceCustomLabelsForExternalDashboard
* @function
* @param {string} dashboardId - An external sheet slug. We include this so that is_authenticated() can attach a user to the Request object,
    and we can get the appropriate AlignmentTypes through that User's Organization.
*/
export const getStanceCustomLabelsForExternalDashboard = dashboardId => (
    sendAction(
        DjangIO.app.widgets.models.Dashboard.objects.get(dashboardId),
        loadStanceCustomLabelsActionDict,
        {
            action: "get_stance_custom_labels",
            kwargs: {},
            method: "get",
            payload: {}
        }
    )
)

/**
* This functions calls the update_stance_alert_title action on AlertResource.
*
* @name updateStanceAlertName
* @function
* @param {Number} stance - LegislativeStanceType value corresponding to stance
* @param {String} newLabel - updated label for stance
*/
export const updateStanceAlertName = (stance, newLabel) => (
    sendAction(
        DjangIO.app.userdata.alerts.models.Alert,
        updateStanceAlertNameActionDict,
        {
            action: "update_stance_alert_title",
            kwargs: {
                stance,
                label: newLabel,
            },
            method: "patch",
        }
    )
)
